import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { coreFallbackConfig } from '@account-services-mfe/core';
import { PaymentSecurityScreens } from '../typings';
import getCrossBorderModalContainerId from '../helpers/get-cross-border-modal-container-id';
import CrossBorderModal from '../ui/common/cross-border-modal';
import triggerPaymentSecurityAnalytics from '../helpers/trigger-payment-security-analytics';
import shouldShowCrossBorderModal from '../helpers/should-show-cross-border-modal';
import { PAGE_NAME_BY_SCREEN } from '../const/analytics';
var PaymentSecurityActionTypes;
(function (PaymentSecurityActionTypes) {
    PaymentSecurityActionTypes["CLEAR_ERROR"] = "CLEAR_ERROR";
    PaymentSecurityActionTypes["SET_CHECKBOX_VALUE"] = "SET_CHECKBOX_VALUE";
    PaymentSecurityActionTypes["SET_HAS_ERROR_SUBMIT"] = "SET_HAS_ERROR_SUBMIT";
    PaymentSecurityActionTypes["CHANGE_CURRENT_SCREEN"] = "CHANGE_CURRENT_SCREEN";
})(PaymentSecurityActionTypes || (PaymentSecurityActionTypes = {}));
const reducer = (state, action) => {
    switch (action.type) {
        case PaymentSecurityActionTypes.CLEAR_ERROR:
            return { ...state, hasErrorOnSubmit: false };
        case PaymentSecurityActionTypes.SET_CHECKBOX_VALUE:
            return { ...state, isChecked: Boolean(action.checked) };
        case PaymentSecurityActionTypes.SET_HAS_ERROR_SUBMIT:
            return { ...state, hasErrorOnSubmit: true };
        case PaymentSecurityActionTypes.CHANGE_CURRENT_SCREEN:
            return {
                ...state,
                currentPaymentSecurityScreen: action.newPaymentSecurityScreen
            };
        default:
            return state;
    }
};
export const defaultState = {
    config: coreFallbackConfig,
    selectedAccountId: sessionStorage.getItem('selectedAccountId') ?? '',
    authorizeAONUrl: '',
    handleClose: () => null,
    isChecked: false,
    hasErrorOnSubmit: false,
    updateCheckboxValue: () => { },
    setErrorOnUpdate: () => { },
    clearError: () => { },
    setNewState: () => { },
    isPopupVisible: false,
    showPopup: () => { },
    hidePopup: () => { },
    currentPaymentSecurityScreen: PaymentSecurityScreens.DEFAULT,
    setCurrentPaymentSecurityScreen: () => { },
    tokenDetails: {
        shouldFetchToken: false,
        isFetchingToken: false
    },
    setTokenDetails: () => { }
};
export const PaymentSecurityContext = createContext(defaultState);
PaymentSecurityContext.displayName = 'PaymentSecurityContext';
export const usePaymentSecurityContext = () => {
    const value = useContext(PaymentSecurityContext);
    if (!value) {
        throw new Error('No config');
    }
    return value;
};
const PaymentSecurityContextProvider = ({ initialState, children, customState }) => {
    const [state, dispatch] = useReducer(reducer, {
        ...defaultState,
        ...initialState,
        ...customState
    });
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [tokenDetails, setTokenDetails] = useState({
        shouldFetchToken: false,
        isFetchingToken: false
    });
    const popupAnchorId = useMemo(() => getCrossBorderModalContainerId(state.selectedAccountId), [state.selectedAccountId]);
    const showPopup = useCallback(() => {
        triggerPaymentSecurityAnalytics({
            pageName: 'exit_confirmation'
        });
        setIsPopupVisible(true);
    }, [setIsPopupVisible]);
    const hidePopup = useCallback(() => {
        triggerPaymentSecurityAnalytics({
            pageName: PAGE_NAME_BY_SCREEN[state.currentPaymentSecurityScreen]
        });
        setIsPopupVisible(false);
    }, [setIsPopupVisible, state.currentPaymentSecurityScreen]);
    const setCurrentPaymentSecurityScreen = useCallback((newPaymentScreen) => {
        dispatch({
            type: PaymentSecurityActionTypes.CHANGE_CURRENT_SCREEN,
            checked: undefined,
            newPaymentSecurityScreen: newPaymentScreen
        });
    }, []);
    const clearError = useCallback(() => {
        dispatch({
            type: PaymentSecurityActionTypes.CLEAR_ERROR
        });
    }, []);
    const setErrorOnUpdate = useCallback(() => {
        dispatch({
            type: PaymentSecurityActionTypes.SET_HAS_ERROR_SUBMIT
        });
    }, []);
    const updateCheckboxValue = useCallback((checked) => {
        dispatch({
            type: PaymentSecurityActionTypes.SET_CHECKBOX_VALUE,
            checked
        });
    }, []);
    const contextValue = useMemo(() => ({
        ...state,
        clearError,
        setErrorOnUpdate,
        updateCheckboxValue,
        showPopup,
        hidePopup,
        isPopupVisible,
        setCurrentPaymentSecurityScreen,
        tokenDetails,
        setTokenDetails
    }), [
        state,
        clearError,
        setErrorOnUpdate,
        updateCheckboxValue,
        showPopup,
        hidePopup,
        isPopupVisible,
        setCurrentPaymentSecurityScreen,
        tokenDetails,
        setTokenDetails
    ]);
    return (_jsxs(PaymentSecurityContext.Provider, { value: contextValue, children: [children, shouldShowCrossBorderModal(state.currentPaymentSecurityScreen) && (_jsxs(_Fragment, { children: [_jsx("div", { id: popupAnchorId, "data-testid": popupAnchorId }), _jsx(CrossBorderModal, { tokenDetails: tokenDetails, setTokenDetails: setTokenDetails, isOpen: isPopupVisible, containerId: popupAnchorId, closeModal: hidePopup })] }))] }));
};
export default PaymentSecurityContextProvider;
